import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowLeftRight, ArrowRight, DatabaseBackup, Download, Grid3x3, LayoutGrid, MapPin, Plus, RotateCcw, Trash2, Upload } from "lucide-react";
import { useConfirm } from "../../components/ConfirmProvider";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import CategoryCard, { CategoryGroupDialog } from "./CategoryCard";
import ColorSetDialog from "./ColorSetDialog";
import HistoryDialog from "./HistoryDialog";
import IssueDialog from "./IssueDialog";
import ItemDialog from "./ItemDialog";
import LocationsManager from "./LocationsManager";
import PhotoGalleryDialog from "./PhotoGalleryDialog";
import RepairEquipmentDialog from "./RepairEquipmentDialog";
import ShelfGrid from "./ShelfGrid";
import TransfersHistoryDialog from "./TransfersHistoryDialog";
import TypeSummaryCards from "./TypeSummaryCards";
import { ITEM_TYPES, TYPE_LABELS } from "./constants";
import { groupItemsByCategory } from "./helpers";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { psaCatRank } from "../../lib/psaConstants";

export default function LocationsView({ meta, items, stores, cars, onChanged }) {  const confirm = useConfirm();
  const { canDo, isAdmin, user } = useAuth();
  const [dialog, setDialog] = useState(null); // {mode, item?}
  const [historyFor, setHistoryFor] = useState(null);
  const [repairFor, setRepairFor] = useState(null);
  const [issueFor, setIssueFor] = useState(null);
  const [photoFor, setPhotoFor] = useState(null);
  const [viewMode, setViewMode] = useState({}); // { [loc]: "cards" | "shelf" }
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState("merge");
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [backupsOpen, setBackupsOpen] = useState(false);
  const [backups, setBackups] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [transfersOpen, setTransfersOpen] = useState(false);
  const [categoryGroup, setCategoryGroup] = useState(null); // { location, name, items }
  const [noLocSelected, setNoLocSelected] = useState(() => new Set()); // ids selected for bulk move
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  const [bulkMoveTarget, setBulkMoveTarget] = useState("");
  const [showColorSet, setShowColorSet] = useState(null); // { code, label, hex }
  const [bulkMoving, setBulkMoving] = useState(false);

  // Clear selection when list changes (items moved, refresh, etc.)
  useEffect(() => {
    if (noLocSelected.size === 0) return;
    const currentIds = new Set((items || []).filter((i) => !i.location).map((i) => i.id));
    let changed = false;
    const next = new Set();
    for (const id of noLocSelected) {
      if (currentIds.has(id)) next.add(id);
      else changed = true;
    }
    if (changed) setNoLocSelected(next);
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleNoLocSel = (id) => {
    setNoLocSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const performBulkMove = async () => {
    if (!bulkMoveTarget || noLocSelected.size === 0) return;
    setBulkMoving(true);
    try {
      const ids = Array.from(noLocSelected);
      await Promise.all(
        ids.map(async (id) => {
          const it = (items || []).find((x) => x.id === id);
          if (!it) return;
          const payload = {
            name: it.name || "",
            item_type: it.item_type || "echipament",
            location: bulkMoveTarget,
            color: it.color || "",
            shelf_col: it.shelf_col || "",
            shelf_row: it.shelf_row || "",
            status: it.status || "functional",
            quantity: it.quantity ?? 1,
            unit: it.unit || "",
            warranty_until: it.warranty_until || "",
            purchase_date: it.purchase_date || "",
            store_id: it.store_id || "",
            linked_car_id: it.linked_car_id || "",
            linked_equipment_id: it.linked_equipment_id || "",
            is_oil: !!it.is_oil,
            car_consumable_type: it.car_consumable_type || null,
            serial: it.serial || "",
            no_serial: !!it.no_serial,
            note: it.note || "",
          };
          await api.put(`/inventory/${id}`, payload);
        })
      );
      toast.success(`${ids.length} ${ids.length === 1 ? "articol mutat" : "articole mutate"} în "${bulkMoveTarget}"`);
      setNoLocSelected(new Set());
      setBulkMoveOpen(false);
      setBulkMoveTarget("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la mutare");
    } finally {
      setBulkMoving(false);
    }
  };

  // Auto-open add dialog when navigated with #new
  useEffect(() => {
    if (window.location.hash === "#new" && canDo("INVENTAR", "add")) {
      setDialog({ mode: "add" });
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [canDo]);

  // Auto-open gallery when arriving with ?open={iid} (QR scan)
  const [searchParamsLV] = useSearchParams();
  useEffect(() => {
    const openId = searchParamsLV.get("open");
    if (openId && items.length > 0) {
      const match = items.find((it) => it.id === openId);
      if (match) setPhotoFor(match);
    }
  }, [searchParamsLV, items]);

  const setMode = (loc, mode) => setViewMode((v) => ({ ...v, [loc]: mode }));

  // Per-location filter by item_type ("echipament" / "psa" / "consumabil"). null = no filter.
  const [typeFilter, setTypeFilter] = useState({}); // { [location]: type | null }
  const setLocTypeFilter = (loc, t) => setTypeFilter((v) => ({ ...v, [loc]: v[loc] === t ? null : t }));

  // For each location, count items per item_type for the type-summary cards
  const typeCountsByLoc = useMemo(() => {
    const out = {};
    for (const it of items) {
      const loc = it.location || "__none__";
      const t = it.item_type || "echipament";
      out[loc] ??= {};
      out[loc][t] = (out[loc][t] || 0) + 1;
    }
    return out;
  }, [items]);

  const grouped = useMemo(() => {
    const out = {};
    for (const loc of meta.locations) out[loc] = items.filter((i) => i.location === loc).sort((a,b) => a.name.localeCompare(b.name));
    // Items truly fără locație ȘI fără assignee (cazul "Fără locație atribuită")
    out.__none__ = items.filter((i) => !i.location && !i.assignee_person_id).sort((a,b) => a.name.localeCompare(b.name));
    return out;
  }, [items, meta]);

  // Grupare PSA-uri predate angajaților care nu mai au locație → afișate pe persoane
  // (items cu assignee_person_id setat, indiferent dacă au sau nu location)
  const assignedByPerson = useMemo(() => {
    const map = new Map(); // person_id -> { name, items: [] }
    for (const it of items) {
      if (!it.assignee_person_id) continue;
      // Considerăm „predat la angajat" doar items fără locație fizică (sau orice item asignat —
      // alegerea: dacă are location ȘI assignee, îl ținem pe locația respectivă; aici grupăm
      // doar pe cei fără locație, ca să nu dublăm)
      if (it.location) continue;
      const pid = it.assignee_person_id;
      const pname = it.assignee || "(angajat necunoscut)";
      if (!map.has(pid)) map.set(pid, { person_id: pid, name: pname, items: [] });
      map.get(pid).items.push(it);
    }
    // Sortare itemi după categorie PSA (mască → furtun → curea → cizme → hanorac → tricou...)
    // și sortare persoane alfabetic după nume
    const out = Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    for (const p of out) {
      p.items.sort((a, b) => {
        const ra = psaCatRank(a.psa_category);
        const rb = psaCatRank(b.psa_category);
        if (ra !== rb) return ra - rb;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
    }
    return out;
  }, [items]);

  const handleExportDb = async () => {
    try {
      const { data } = await api.get("/inventory/export-db");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `inventar-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export reușit: ${data.counts?.items || 0} produse`);
    } catch {
      toast.error("Eroare la export");
    }
  };

  const handleImportPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        if (!parsed.items || !Array.isArray(parsed.items)) throw new Error("Fișier invalid (lipsește 'items')");
        setImportFile(f);
        setImportPreview(parsed);
      } catch (err) {
        toast.error("Fișier JSON invalid: " + err.message);
      }
    };
    reader.readAsText(f);
    e.target.value = "";
  };

  const handleImportConfirm = async () => {
    if (!importPreview) return;
    setImporting(true);
    try {
      const { data } = await api.post("/inventory/import-db", {
        mode: importMode,
        items: importPreview.items,
        stores: importPreview.stores || null,
      });
      toast.success(`Import reușit (${importMode}): ${data.imported_items} produse · total acum: ${data.total_items}`);
      setImportFile(null);
      setImportPreview(null);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la import");
    } finally {
      setImporting(false);
    }
  };

  const loadBackups = async () => {
    try {
      const { data } = await api.get("/inventory/backups");
      setBackups(data || []);
    } catch {
      toast.error("Eroare la încărcarea backup-urilor");
    }
  };

  const openBackups = async () => {
    setBackupsOpen(true);
    setBackups(null);
    await loadBackups();
  };

  const runBackupNow = async () => {
    setBackupBusy(true);
    try {
      const { data } = await api.post("/inventory/backups/run-now");
      toast.success(`Backup salvat: ${data.items_count} produse`);
      await loadBackups();
    } catch {
      toast.error("Eroare la backup");
    } finally {
      setBackupBusy(false);
    }
  };

  const deleteBackup = async (bid) => {
    if (!(await confirm({ body: "Ștergi acest backup? Acțiunea e ireversibilă.", danger: true }))) return;
    try {
      await api.delete(`/inventory/backups/${bid}`);
      toast.success("Backup șters");
      await loadBackups();
    } catch {
      toast.error("Eroare la ștergere");
    }
  };

  const confirmRestoreFrom = async () => {
    if (!confirmRestore) return;
    setBackupBusy(true);
    try {
      const { data } = await api.post(`/inventory/backups/${confirmRestore.id}/restore`);
      toast.success(`Restaurare reușită: ${data.restored_items} produse`);
      setConfirmRestore(null);
      setBackupsOpen(false);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la restaurare");
    } finally {
      setBackupBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-zinc-500">
          Vizualizare grupată după <strong>locație</strong>. Statusul echipamentului: <span className="text-[var(--green-valid)] font-semibold">verde</span> = funcțional, <span className="text-[var(--red-bila)] font-semibold">roșu</span> = defect.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportDb}
                data-testid="export-db-btn"
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              >
                <Download size={14} className="mr-1.5"/> Export bază date
              </Button>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".json,application/json"
                  className="hidden"
                  onChange={handleImportPick}
                  data-testid="import-db-input"
                />
                <span className="inline-flex items-center gap-1.5 border border-indigo-300 text-indigo-700 hover:bg-indigo-50 rounded-md px-3 py-1.5 text-sm font-medium h-9">
                  <Upload size={14}/> Import bază date
                </span>
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={openBackups}
                data-testid="open-backups-btn"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                title="Backup-uri automate zilnice (retenție 30 zile)"
              >
                <DatabaseBackup size={14} className="mr-1.5"/> Backup-uri auto
              </Button>
            </>
          )}
          {(isAdmin || user?.role === "magazioner") && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTransfersOpen(true)}
              data-testid="open-transfers-history-btn"
              className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
              title="Istoric preluări / predări echipamente"
            >
              <ArrowLeftRight size={14} className="mr-1.5"/> Istoric transferuri
            </Button>
          )}
          {canDo("INVENTAR", "add") && (
            <Button onClick={() => setDialog({ mode: "add" })} className="bg-[var(--primary)]" data-testid="add-item-btn">
              <Plus size={16}/> Adaugă Produs
            </Button>
          )}
        </div>
      </div>

      {/* Color legend — clickable to see all items in that color set */}
      <div className="card-flat p-3" data-testid="color-legend">
        <div className="small-label mb-2">Seturi de culori (click pentru a vedea echipamentele):</div>
        <div className="flex flex-wrap items-center gap-2">
          {meta.colors.map((c) => {
            const count = items.filter((it) => it.color === c.code).length;
            return (
              <button
                key={c.code}
                type="button"
                onClick={() => setShowColorSet(c)}
                className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border-2 transition-all hover:scale-105 hover:shadow-md font-semibold"
                style={{ borderColor: c.hex, color: c.hex }}
                data-testid={`color-legend-${c.code}`}
                title={`Vezi cele ${count} echipamente din setul ${c.label}`}
              >
                <span className="w-3.5 h-3.5 rounded-full" style={{ background: c.hex }}/>
                {c.label}
                <span className="text-zinc-500 font-normal">({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Manager locații custom */}
      {canDo("INVENTAR", "edit") && (
        <LocationsManager meta={meta} onChanged={onChanged} />
      )}

      {/* Banner global: item-uri fără nr de serie */}
      {(() => {
        const missingSN = items.filter((it) => !it.serial && !it.no_serial);
        if (missingSN.length === 0) return null;
        return (
          <div className="rounded-md border-2 border-amber-400 bg-amber-50 p-3 flex items-start gap-3" data-testid="missing-serial-banner">
            <span className="text-2xl flex-shrink-0">⚠️</span>
            <div className="flex-1">
              <div className="font-bold text-amber-900 text-sm">
                {missingSN.length} echipament{missingSN.length === 1 ? "" : "e"} fără nr. de serie
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                Nr. de serie este obligatoriu pentru fiecare echipament. Editează fiecare item pentru a-l completa sau bifează „NU are nr de serie" dacă nu este aplicabil.
              </div>
              <details className="mt-2">
                <summary className="text-xs text-amber-900 cursor-pointer hover:underline">Vezi lista ({missingSN.length})</summary>
                <ul className="mt-2 text-xs text-amber-900 space-y-0.5 max-h-[200px] overflow-auto pl-4">
                  {missingSN.map((it) => (
                    <li key={it.id} className="list-disc">{it.name} <span className="text-amber-700">· {it.location || "fără locație"}</span></li>
                  ))}
                </ul>
              </details>
            </div>
          </div>
        );
      })()}

      {meta.locations.map((loc) => {
        const inLocAll = grouped[loc] || [];
        const tFilter = typeFilter[loc] || null;
        const inLoc = tFilter ? inLocAll.filter((it) => (it.item_type || "echipament") === tFilter) : inLocAll;
        const typeCounts = typeCountsByLoc[loc] || {};
        const isShelfLoc = meta.shelf_locations.includes(loc);
        const mode = viewMode[loc] || "cards";
        const showShelf = isShelfLoc && mode === "shelf";
        return (
          <div key={loc} className="card-flat p-4">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <MapPin size={18} className="text-[var(--primary)]"/>
                <h3 className="font-display text-lg font-bold">{loc}</h3>
                <span className="text-xs text-zinc-500">({inLocAll.length})</span>
                {isShelfLoc && <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 font-semibold">CU RAFTURI</span>}
              </div>
              {isShelfLoc && (
                <div className="flex items-center gap-1 bg-zinc-100 rounded-md p-0.5" data-testid={`view-toggle-${loc}`}>
                  <button
                    onClick={() => setMode(loc, "shelf")}
                    className={`text-[11px] px-2 py-1 rounded font-semibold transition-colors flex items-center gap-1 ${mode === "shelf" ? "bg-white shadow-sm text-[var(--primary)]" : "text-zinc-500 hover:text-zinc-900"}`}
                    title="Vizualizare grafică rafturi"
                    data-testid={`view-shelf-${loc}`}
                  >
                    <Grid3x3 size={12}/> Raft
                  </button>
                  <button
                    onClick={() => setMode(loc, "cards")}
                    className={`text-[11px] px-2 py-1 rounded font-semibold transition-colors flex items-center gap-1 ${mode === "cards" ? "bg-white shadow-sm text-[var(--primary)]" : "text-zinc-500 hover:text-zinc-900"}`}
                    title="Vizualizare carduri"
                    data-testid={`view-cards-${loc}`}
                  >
                    <LayoutGrid size={12}/> Carduri
                  </button>
                </div>
              )}
            </div>
            {/* Type summary cards (group by item_type) */}
            {Object.keys(typeCounts).length > 0 && (
              <TypeSummaryCards
                loc={loc}
                items={inLocAll}
                typeCounts={typeCounts}
                active={tFilter}
                onToggle={(t) => setLocTypeFilter(loc, t)}
              />
            )}
            {showShelf ? (
              <ShelfGrid
                items={inLoc}
                meta={meta}
                onItemClick={(it) => setDialog({ mode: "edit", item: it })}
                onEmptyClick={canDo("INVENTAR", "add") ? (row, col) => setDialog({ mode: "add", prefill: { location: loc, shelf_row: row, shelf_col: String(col) } }) : null}
              />
            ) : inLoc.length === 0 ? (
              <div className="text-sm text-zinc-400 italic">{tFilter ? `Niciun articol de tip "${TYPE_LABELS[tFilter] || tFilter}" în această locație.` : "Nimic stocat aici."}</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {groupItemsByCategory(inLoc).map((g) => (
                  <CategoryCard
                    key={`${loc}-${g.key}`}
                    group={g}
                    meta={meta}
                    onOpen={() => setCategoryGroup({
                      location: loc,
                      name: g.name,
                      psa_category: g.psa_category,
                      psa_size: g.psa_size,
                      items: g.items,
                    })}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Items predate angajaților (fără locație fizică) — grupate pe persoană */}
      {assignedByPerson.length > 0 && (
        <div className="space-y-3" data-testid="assigned-to-persons-section">
          <div className="flex items-center gap-2 px-1">
            <span className="text-xs uppercase tracking-wider font-bold text-emerald-800">
              👤 Predate la angajați
            </span>
            <span className="text-xs text-zinc-500">
              ({assignedByPerson.length} {assignedByPerson.length === 1 ? "angajat" : "angajați"} · {assignedByPerson.reduce((s, p) => s + p.items.length, 0)} echipamente)
            </span>
          </div>
          {assignedByPerson.map((p) => (
            <div key={p.person_id} className="card-flat p-4 border-l-4 border-l-emerald-500" data-testid={`assigned-person-${p.person_id}`}>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">
                  👤 {p.name}
                </span>
                <span className="text-xs text-zinc-500">
                  {p.items.length} {p.items.length === 1 ? "echipament" : "echipamente"}
                </span>
                <a
                  href={`/personal/${p.person_id}`}
                  className="ml-auto text-[11px] text-cyan-700 hover:text-cyan-900 hover:underline"
                  data-testid={`assigned-person-link-${p.person_id}`}
                >
                  → Deschide fișa angajatului
                </a>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {p.items.map((it) => (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => setDialog({ mode: "edit", item: it })}
                    className="relative border rounded-md p-3 bg-white text-left transition-all border-zinc-200 hover:border-emerald-500"
                    data-testid={`assigned-item-card-${it.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className="w-2 h-2 rounded-full mt-1 flex-shrink-0"
                        style={{ background: it.status === "defect" ? "var(--red-bila)" : "var(--green-valid)" }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate" title={it.name}>{it.name}</div>
                        {it.serial && (
                          <div className="text-[10px] text-zinc-600 font-mono truncate mt-0.5" title={`Serie: ${it.serial}`}>
                            SN: {it.serial}
                          </div>
                        )}
                        {it.psa_size && (
                          <div className="text-[10px] text-cyan-700 font-semibold truncate mt-0.5">măs. {it.psa_size}</div>
                        )}
                        {it.item_type === "psa" && (
                          <div className="text-[9px] inline-block mt-0.5 px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800 font-bold">PSA</div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {(grouped.__none__ || []).length > 0 && (
        <div className="card-flat p-4 border-dashed" data-testid="no-location-section">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <AlertTriangle size={16} className="text-[var(--orange-bila)]"/>
            <h3 className="font-display text-sm font-bold">Fără locație atribuită</h3>
            <span className="text-xs text-zinc-500">({grouped.__none__.length})</span>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              {canDo("INVENTAR", "edit") && (
                <>
                  {noLocSelected.size > 0 ? (
                    <>
                      <span className="text-xs font-bold text-amber-800 bg-amber-100 border border-amber-200 px-2 py-1 rounded">
                        {noLocSelected.size} selectat{noLocSelected.size === 1 ? "" : "e"}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setNoLocSelected(new Set())}
                        data-testid="no-loc-clear-sel"
                      >
                        Deselectează
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setBulkMoveTarget(meta.locations.includes("MAGAZIE") ? "MAGAZIE" : (meta.locations[0] || ""));
                          setBulkMoveOpen(true);
                        }}
                        className="bg-[var(--primary)] hover:bg-[var(--primary-hover)]"
                        data-testid="no-loc-bulk-move-btn"
                      >
                        <ArrowRight size={14}/> MUTĂ
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setNoLocSelected(new Set((grouped.__none__ || []).map((i) => i.id)));
                      }}
                      data-testid="no-loc-select-all"
                    >
                      Selectează tot
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {(grouped.__none__ || []).map((it) => {
              const selected = noLocSelected.has(it.id);
              return (
                <div
                  key={it.id}
                  className={`relative border rounded-md p-3 bg-white transition-all ${
                    selected ? "border-[var(--primary)] ring-2 ring-[var(--primary)] ring-opacity-30 shadow-sm" : "border-zinc-200 hover:border-[var(--primary)]"
                  }`}
                  data-testid={`no-loc-card-${it.id}`}
                >
                  {canDo("INVENTAR", "edit") && (
                    <label
                      className="absolute top-2 right-2 flex items-center justify-center cursor-pointer z-10"
                      onClick={(e) => e.stopPropagation()}
                      title={selected ? "Deselectează" : "Selectează pentru mutare"}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleNoLocSel(it.id)}
                        data-testid={`no-loc-check-${it.id}`}
                        className="w-4 h-4 accent-[var(--primary)] cursor-pointer"
                      />
                    </label>
                  )}
                  <button
                    type="button"
                    onClick={() => setDialog({ mode: "edit", item: it })}
                    className="w-full text-left"
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className="w-2 h-2 rounded-full mt-1 flex-shrink-0"
                        style={{ background: it.status === "defect" ? "var(--red-bila)" : "var(--green-valid)" }}
                      />
                      <div className="flex-1 min-w-0 pr-6">
                        <div className="text-sm font-semibold truncate" title={it.name}>{it.name}</div>
                        {it.serial && (
                          <div className="text-[10px] text-zinc-600 font-mono truncate mt-0.5" title={`Serie: ${it.serial}`}>
                            SN: {it.serial}
                          </div>
                        )}
                        {!it.serial && it.no_serial && (
                          <div className="text-[10px] text-zinc-400 italic truncate mt-0.5">SN: fără serie</div>
                        )}
                        {!it.serial && !it.no_serial && (
                          <div className="text-[10px] mt-0.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold border border-amber-300 inline-block" title="Nr. de serie lipsește. Editează pentru a completa.">
                            ⚠️ Lipsă nr. serie
                          </div>
                        )}
                        <div className="text-[10px] text-zinc-500 mt-0.5">
                          {ITEM_TYPES.find((t) => t.code === it.item_type)?.label || it.item_type}
                          {it.quantity > 1 ? ` · ${it.quantity}${it.unit ? " " + it.unit : ""}` : ""}
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bulk move dialog */}
      {bulkMoveOpen && (
        <Dialog open onOpenChange={(v) => { if (!v) { setBulkMoveOpen(false); setBulkMoveTarget(""); } }}>
          <DialogContent data-testid="bulk-move-dialog" className="max-w-md">
            <DialogHeader>
              <DialogTitle>Mută {noLocSelected.size} {noLocSelected.size === 1 ? "articol" : "articole"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="text-xs text-zinc-600">
                Selectează locația de destinație pentru articolele alese.
              </div>
              <div>
                <Label className="small-label">Locație destinație</Label>
                <Select value={bulkMoveTarget} onValueChange={setBulkMoveTarget}>
                  <SelectTrigger className="mt-1" data-testid="bulk-move-target">
                    <SelectValue placeholder="Alege locația..."/>
                  </SelectTrigger>
                  <SelectContent>
                    {meta.locations.map((loc) => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {noLocSelected.size <= 10 && (
                <div className="max-h-40 overflow-y-auto text-xs text-zinc-600 border border-zinc-200 rounded p-2 bg-zinc-50">
                  <div className="font-bold uppercase text-[10px] tracking-wider mb-1">Articole:</div>
                  {Array.from(noLocSelected).map((id) => {
                    const it = (items || []).find((x) => x.id === id);
                    return <div key={id} className="truncate">• {it?.name || id}</div>;
                  })}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setBulkMoveOpen(false); setBulkMoveTarget(""); }}>
                Anulează
              </Button>
              <Button
                onClick={performBulkMove}
                disabled={!bulkMoveTarget || bulkMoving}
                className="bg-[var(--primary)] hover:bg-[var(--primary-hover)]"
                data-testid="bulk-move-confirm"
              >
                {bulkMoving ? "Se mută..." : `Mută în ${bulkMoveTarget || "..."}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {dialog && (
        <ItemDialog
          mode={dialog.mode} item={dialog.item} prefill={dialog.prefill} meta={meta} stores={stores} cars={cars}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); onChanged(); }}
        />
      )}
      {historyFor && <HistoryDialog item={historyFor} onClose={() => setHistoryFor(null)} />}
      {repairFor && <RepairEquipmentDialog item={repairFor} allItems={items} onClose={() => setRepairFor(null)} onDone={() => { setRepairFor(null); onChanged(); }} />}
      {issueFor && <IssueDialog item={issueFor} onClose={() => setIssueFor(null)} onDone={() => { setIssueFor(null); onChanged(); }} />}
      {photoFor && <PhotoGalleryDialog item={photoFor} onClose={() => setPhotoFor(null)} onChanged={onChanged} />}

      {categoryGroup && (
        <CategoryGroupDialog
          group={categoryGroup}
          meta={meta}
          onClose={() => setCategoryGroup(null)}
          onMove={(it) => { setCategoryGroup(null); setDialog({ mode: "move", item: it }); }}
          onEdit={(it) => { setCategoryGroup(null); setDialog({ mode: "edit", item: it }); }}
          onHistory={(it) => { setCategoryGroup(null); setHistoryFor(it); }}
          onRepair={(it) => { setCategoryGroup(null); setRepairFor(it); }}
          onIssue={(it) => { setCategoryGroup(null); setIssueFor(it); }}
          onPhotos={(it) => { setCategoryGroup(null); setPhotoFor(it); }}
        />
      )}

      {importPreview && (
        <Dialog open onOpenChange={(v) => { if (!v && !importing) { setImportFile(null); setImportPreview(null); } }}>
          <DialogContent className="max-w-md" data-testid="import-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Upload size={18} className="text-indigo-600"/> Import bază de date inventar</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="bg-zinc-50 rounded-md p-3 text-xs space-y-1 border">
                <div><strong>Fișier:</strong> {importFile?.name}</div>
                <div><strong>Produse în fișier:</strong> {importPreview.counts?.items ?? importPreview.items?.length ?? 0}</div>
                <div><strong>Magazine în fișier:</strong> {importPreview.counts?.stores ?? importPreview.stores?.length ?? 0}</div>
                {importPreview.exported_at && <div className="text-zinc-500">Exportat: {String(importPreview.exported_at).slice(0, 19).replace("T", " ")}{importPreview.exported_by ? ` · de ${importPreview.exported_by}` : ""}</div>}
              </div>

              <div>
                <Label className="small-label">Mod import</Label>
                <div className="flex gap-2 mt-1.5">
                  <label className={`flex-1 border rounded-md p-2.5 cursor-pointer transition-colors ${importMode === "merge" ? "border-indigo-500 bg-indigo-50" : "border-zinc-200 hover:border-zinc-300"}`}>
                    <input type="radio" name="import-mode" value="merge" checked={importMode === "merge"} onChange={() => setImportMode("merge")} className="sr-only"/>
                    <div className="text-xs font-bold">MERGE</div>
                    <div className="text-[10px] text-zinc-500">Adaugă / actualizează. Păstrează ce există.</div>
                  </label>
                  <label className={`flex-1 border rounded-md p-2.5 cursor-pointer transition-colors ${importMode === "replace" ? "border-red-500 bg-red-50" : "border-zinc-200 hover:border-zinc-300"}`}>
                    <input type="radio" name="import-mode" value="replace" checked={importMode === "replace"} onChange={() => setImportMode("replace")} className="sr-only"/>
                    <div className="text-xs font-bold text-red-700">REPLACE</div>
                    <div className="text-[10px] text-zinc-500">Șterge tot și importă. Ireversibil!</div>
                  </label>
                </div>
              </div>

              {importMode === "replace" && (
                <div className="bg-red-50 border border-red-200 rounded-md p-2.5 text-xs text-red-800">
                  ⚠️ Toate produsele actuale ({items.length}) vor fi <strong>șterse definitiv</strong> și înlocuite cu cele din fișier.
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setImportFile(null); setImportPreview(null); }} disabled={importing}>Anulează</Button>
              <Button
                onClick={handleImportConfirm}
                disabled={importing}
                className={importMode === "replace" ? "bg-red-600 hover:bg-red-700" : "bg-indigo-600 hover:bg-indigo-700"}
                data-testid="import-confirm-btn"
              >
                <Upload size={14} className="mr-1.5"/>{importing ? "Import…" : `Confirmă ${importMode === "replace" ? "înlocuire" : "merge"}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {backupsOpen && (
        <Dialog open onOpenChange={(v) => { if (!v) setBackupsOpen(false); }}>
          <DialogContent className="max-w-2xl" data-testid="backups-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DatabaseBackup size={18} className="text-amber-600"/> Backup-uri automate inventar
              </DialogTitle>
            </DialogHeader>
            <div className="text-xs text-zinc-600 -mt-2 mb-1">
              Backup automat zilnic la <strong>03:00 UTC</strong> · retenție <strong>30 zile</strong> · poți salva și manual oricând.
            </div>

            <div className="flex justify-end mb-2">
              <Button
                size="sm"
                variant="outline"
                onClick={runBackupNow}
                disabled={backupBusy}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="run-backup-now-btn"
              >
                <DatabaseBackup size={14} className="mr-1.5"/>{backupBusy ? "Salvez…" : "Salvează backup acum"}
              </Button>
            </div>

            <div className="max-h-[400px] overflow-y-auto border border-zinc-200 rounded-md">
              {backups === null ? (
                <div className="p-6 text-center text-sm text-zinc-400">Se încarcă…</div>
              ) : backups.length === 0 ? (
                <div className="p-6 text-center text-sm text-zinc-500">Niciun backup salvat încă. Apasă <strong>"Salvează backup acum"</strong>.</div>
              ) : (
                <div className="divide-y">
                  {backups.map((b) => (
                    <div key={b.id} className="flex items-center justify-between p-3 hover:bg-zinc-50 transition-colors" data-testid={`backup-row-${b.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm">
                          <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${
                            b.reason === "auto" ? "bg-amber-100 text-amber-800"
                            : b.reason === "manual" ? "bg-blue-100 text-blue-800"
                            : "bg-orange-100 text-orange-800"
                          }`}>
                            {b.reason === "auto" ? "Auto" : b.reason === "manual" ? "Manual" : "Pre-restore"}
                          </span>
                          <span className="font-mono text-zinc-700">{String(b.created_at).slice(0, 16).replace("T", " ")}</span>
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">
                          📦 {b.items_count} produse{b.stores_count > 0 ? ` · 🏪 ${b.stores_count} magazine` : ""}
                        </div>
                      </div>
                      <div className="flex gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmRestore(b)}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          data-testid={`restore-${b.id}`}
                        >
                          <RotateCcw size={12} className="mr-1"/> Restaurează
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteBackup(b.id)}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                          title="Șterge backup"
                        >
                          <Trash2 size={12}/>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {confirmRestore && (
        <Dialog open onOpenChange={(v) => { if (!v && !backupBusy) setConfirmRestore(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-700">
                <RotateCcw size={18}/> Restaurează inventar
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
                ⚠️ Această acțiune va <strong>înlocui tot inventarul curent</strong> cu cel din backup.
                <br />
                Un snapshot al stării curente va fi salvat automat înainte (ca <strong>"pre-restore"</strong>), deci poți reveni la ce ai acum dacă te răzgândești.
              </div>
              <div className="bg-zinc-50 border rounded-md p-3 text-xs space-y-1">
                <div><strong>Backup:</strong> {String(confirmRestore.created_at).slice(0, 19).replace("T", " ")}</div>
                <div><strong>Conține:</strong> {confirmRestore.items_count} produse, {confirmRestore.stores_count} magazine</div>
                <div><strong>Tip:</strong> {confirmRestore.reason}</div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmRestore(null)} disabled={backupBusy}>Anulează</Button>
              <Button
                onClick={confirmRestoreFrom}
                disabled={backupBusy}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-restore-btn"
              >
                <RotateCcw size={14} className="mr-1.5"/>{backupBusy ? "Restaurez…" : "Confirmă restaurare"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {transfersOpen && <TransfersHistoryDialog onClose={() => setTransfersOpen(false)} />}

      {/* Dialog: vezi toate echipamentele alocate la o culoare + istoricul */}
      <ColorSetDialog colorMeta={showColorSet} items={items} cars={cars} onClose={() => setShowColorSet(null)} />
    </div>
  );
}
